"use client"

import Image from "next/image"
import CartTotals from "@modules/common/components/cart-totals"
import DiscountCode from "@modules/checkout/components/discount-code"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { PAYMENT_ICONS } from "@constants/navigation"
import { FREE_DELIVERY_THRESHOLD } from "@constants/cart"
import { convertToLocale } from "@lib/util/money"
import { getCheckoutStep } from "@lib/util/get-checkout-step"
import { getShippingEstimate } from "@lib/util/shipping-estimate"
import { HttpTypes } from "@medusajs/types"
import { Truck } from "lucide-react"

type SummaryProps = {
  cart: HttpTypes.StoreCart & {
    promotions: HttpTypes.StorePromotion[]
  }
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
}

const Summary = ({ cart, shippingOptions = [] }: SummaryProps) => {
  const step = getCheckoutStep(cart)
  const currencyCode = cart.currency_code

  // A shipping method is only attached once the customer reaches checkout —
  // before that, show the cheapest available option as an estimate instead
  // of hiding the shipping line, matching the reference site and the cart
  // drawer (see getShippingEstimate).
  const {
    label: shippingLabel,
    amount: shippingEstimate,
    isAttached: hasShippingMethod,
  } = getShippingEstimate(cart, shippingOptions)

  const displayTotals = hasShippingMethod
    ? cart
    : {
        ...cart,
        shipping_subtotal: shippingEstimate,
        total: (cart.total ?? 0) + shippingEstimate,
      }

  // Free delivery progress — uses item_subtotal (before shipping/tax) to avoid circular logic
  // Stub: threshold is hardcoded. TODO: Replace with ShippingPriceNudge when backend shipping rules are configured.
  const itemSubtotal = cart.item_subtotal || 0
  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - itemSubtotal)
  const progress = FREE_DELIVERY_THRESHOLD > 0
    ? Math.min(100, (itemSubtotal / FREE_DELIVERY_THRESHOLD) * 100)
    : 100

  return (
    <div className="lg:sticky lg:top-4 self-start">
      <div className="rounded-2xl overflow-hidden bg-[#F6F6F6]">
        <div className="p-4 md:p-6 space-y-4 md:space-y-5">
          {/* Free delivery progress banner */}
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {remaining > 0 ? (
                <p className="text-xs md:text-sm text-gray-700">
                  Spend{" "}
                  <span className="font-bold text-gray-900">
                    {convertToLocale({ amount: remaining, currency_code: currencyCode })}
                  </span>{" "}
                  more and get{" "}
                  <span className="font-bold text-gray-900">FREE</span> UK
                  delivery!
                </p>
              ) : (
                <p className="text-xs md:text-sm text-gray-700 font-semibold">
                  You qualify for <span className="text-gray-900">FREE</span> UK delivery!
                </p>
              )}
              <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-navy rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <Truck className="w-5 h-5 md:w-6 md:h-6 text-brand-navy flex-shrink-0" />
          </div>

          <hr className="border-gray-200" />

          {/* Discount code */}
          <DiscountCode cart={cart} />

          <hr className="border-gray-200" />

          {/* Totals */}
          <CartTotals
            totals={displayTotals}
            shippingLabel={shippingLabel}
            pendingCalculation
          />

          {/* Checkout button */}
          <LocalizedClientLink
            href={"/checkout?step=" + step}
            data-testid="checkout-button"
            className="block w-full py-3 md:py-3.5 bg-brand-navy text-white text-center text-sm md:text-[15px] font-semibold rounded-full hover:bg-brand-primary-dark transition-colors"
          >
            Checkout Securely Now
          </LocalizedClientLink>

          {/* Payment icons */}
          <div
            className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2 pt-1"
            role="list"
            aria-label="Accepted payment methods"
          >
            {PAYMENT_ICONS.map((icon) => (
              <div
                key={icon.id}
                className="flex items-center justify-center w-10 h-7 md:w-12 md:h-8 bg-white rounded-md border border-gray-200"
                role="listitem"
              >
                <Image
                  src={icon.image}
                  alt={icon.name}
                  width={36}
                  height={22}
                  className="h-4 md:h-5 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Summary
